import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Image, Plus, Trash2, Edit, Eye, EyeOff, X, Upload, Link, CheckCircle2, Loader2, Globe, HardDrive, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = `${BACKEND_URL}/api`;

const FlyersSection = () => {
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFlyer, setEditingFlyer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMethod, setUploadMethod] = useState('file');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    event_date: '',
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    loadFlyers();
  }, []);

  const loadFlyers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/flyers`, { params: { active_only: false } });
      setFlyers(response.data);
    } catch (error) {
      console.error('Error loading flyers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      alert('File size must be less than 15MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.url }));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const msg = error.response?.data?.detail || 'Failed to upload image. Please try again.';
      alert(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.image_url) {
      alert('Please fill in the title and provide an image');
      return;
    }

    try {
      if (editingFlyer) {
        await axios.put(`${API}/flyers/${editingFlyer.id}`, formData);
      } else {
        await axios.post(`${API}/flyers`, formData);
      }
      loadFlyers();
      resetForm();
    } catch (error) {
      console.error('Error saving flyer:', error);
      alert('Failed to save flyer');
    }
  };

  const handleDelete = async (flyerId) => {
    if (!window.confirm('Are you sure you want to delete this flyer?')) return;
    try {
      await axios.delete(`${API}/flyers/${flyerId}`);
      loadFlyers();
    } catch (error) {
      console.error('Error deleting flyer:', error);
      alert('Failed to delete flyer');
    }
  };

  const handleToggleActive = async (flyerId) => {
    try {
      await axios.patch(`${API}/flyers/${flyerId}/toggle`);
      loadFlyers();
    } catch (error) {
      console.error('Error toggling flyer status:', error);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', image_url: '', link_url: '', event_date: '', is_active: true, display_order: 0 });
    setEditingFlyer(null);
    setShowAddModal(false);
    setUploadMethod('file');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startEdit = (flyer) => {
    setFormData({
      title: flyer.title,
      description: flyer.description || '',
      image_url: flyer.image_url,
      link_url: flyer.link_url || '',
      event_date: flyer.event_date || '',
      is_active: flyer.is_active,
      display_order: flyer.display_order || 0
    });
    setEditingFlyer(flyer);
    setUploadMethod(flyer.image_url?.startsWith('/api/') ? 'file' : 'url');
    setShowAddModal(true);
  };

  const handleMoveFlyer = async (flyerId, direction) => {
    const idx = flyers.findIndex(f => f.id === flyerId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= flyers.length) return;
    try {
      await Promise.all([
        axios.put(`${API}/flyers/${flyers[idx].id}`, { ...flyers[idx], display_order: swapIdx }),
        axios.put(`${API}/flyers/${flyers[swapIdx].id}`, { ...flyers[swapIdx], display_order: idx })
      ]);
      loadFlyers();
    } catch (error) {
      console.error('Error reordering flyers:', error);
    }
  };

  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-3xl p-6 text-white shadow-xl"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
              <Image className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Vineyard Events</h2>
              <p className="text-amber-100">Upload and manage flyers for the homepage</p>
            </div>
          </div>
          <motion.button
            onClick={() => setShowAddModal(true)}
            data-testid="add-flyer-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors font-medium"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="w-5 h-5" />
            <span>Add Flyer</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Flyers Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
        </div>
      ) : flyers.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-3xl p-12 text-center shadow-lg">
          <Image className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">No Flyers Yet</h3>
          <p className="text-slate-500 mb-6">Upload your first church flyer to display on the homepage</p>
          <motion.button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="w-5 h-5" />
            <span>Add First Flyer</span>
          </motion.button>
        </motion.div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {flyers.map((flyer, index) => (
            <motion.div
              key={flyer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`flyer-card-${flyer.id}`}
              className={`bg-white rounded-2xl overflow-hidden shadow-lg border-2 ${
                flyer.is_active ? 'border-emerald-200' : 'border-slate-200 opacity-60'
              }`}
            >
              <div className="relative h-48 overflow-hidden bg-slate-100">
                <img
                  src={resolveImageUrl(flyer.image_url)}
                  alt={flyer.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'flex items-center justify-center h-full text-slate-400 text-sm';
                    fallback.textContent = 'Image unavailable';
                    e.target.parentElement.appendChild(fallback);
                  }}
                />
                <div className="absolute top-2 left-2 flex gap-1">
                  <span className="w-7 h-7 rounded-lg bg-black/50 backdrop-blur-sm text-white text-xs font-bold flex items-center justify-center" data-testid={`flyer-order-${flyer.id}`}>
                    {index + 1}
                  </span>
                  <button onClick={() => handleMoveFlyer(flyer.id, 'up')} disabled={index === 0}
                    data-testid={`move-flyer-up-${flyer.id}`}
                    className="w-7 h-7 rounded-lg bg-black/50 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleMoveFlyer(flyer.id, 'down')} disabled={index === flyers.length - 1}
                    data-testid={`move-flyer-down-${flyer.id}`}
                    className="w-7 h-7 rounded-lg bg-black/50 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="absolute top-2 right-2 flex gap-2">
                  <span className={`px-2 py-1 rounded-lg text-xs font-bold ${
                    flyer.is_active ? 'bg-emerald-500 text-white' : 'bg-slate-500 text-white'
                  }`}>
                    {flyer.is_active ? 'Active' : 'Hidden'}
                  </span>
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="font-bold text-slate-800 mb-1">{flyer.title}</h3>
                {flyer.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{flyer.description}</p>}
                {flyer.link_url && (
                  <a href={flyer.link_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 mb-3">
                    <Link className="w-3 h-3" /> View Link
                  </a>
                )}
                
                <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                  <motion.button onClick={() => startEdit(flyer)}
                    data-testid={`edit-flyer-${flyer.id}`}
                    className="flex-1 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Edit className="w-4 h-4" /> Edit
                  </motion.button>
                  <motion.button onClick={() => handleToggleActive(flyer.id)}
                    data-testid={`toggle-flyer-${flyer.id}`}
                    className={`flex-1 py-2 rounded-lg transition-colors flex items-center justify-center gap-1 text-sm font-medium ${
                      flyer.is_active ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                    }`}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    {flyer.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {flyer.is_active ? 'Hide' : 'Show'}
                  </motion.button>
                  <motion.button onClick={() => handleDelete(flyer.id)}
                    data-testid={`delete-flyer-${flyer.id}`}
                    className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Trash2 className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500">
                      <Image className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">
                      {editingFlyer ? 'Edit Flyer' : 'Add New Flyer'}
                    </h3>
                  </div>
                  <button onClick={resetForm} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" data-testid="close-flyer-modal">
                    <X className="w-6 h-6 text-slate-600" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-150px)]">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Title *</label>
                  <input
                    type="text"
                    data-testid="flyer-title-input"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:outline-none transition-colors"
                    placeholder="e.g., Sunday Service Announcement"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>

                {/* Image Source Toggle */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Flyer Image *</label>
                  <div className="flex gap-2 mb-3">
                    <button type="button" onClick={() => setUploadMethod('file')}
                      data-testid="upload-method-file"
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        uploadMethod === 'file'
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}>
                      <HardDrive className="w-4 h-4" /> Upload Photo
                    </button>
                    <button type="button" onClick={() => setUploadMethod('url')}
                      data-testid="upload-method-url"
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        uploadMethod === 'url'
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}>
                      <Globe className="w-4 h-4" /> Paste URL
                    </button>
                  </div>

                  {uploadMethod === 'url' ? (
                    <div>
                      <input
                        type="url"
                        data-testid="flyer-image-url-input"
                        className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:outline-none transition-colors text-sm"
                        placeholder="Paste image URL (e.g., https://example.com/flyer.jpg)"
                        value={formData.image_url.startsWith('/api/') ? '' : (formData.image_url || '')}
                        onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                      />
                      <p className="text-xs text-slate-400 mt-1.5">Paste a direct link to an image hosted online</p>
                    </div>
                  ) : (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="flyer-upload"
                        data-testid="flyer-file-input"
                      />
                      <label htmlFor="flyer-upload"
                        className={`block w-full border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${
                          formData.image_url
                            ? 'border-emerald-300 bg-emerald-50'
                            : 'border-slate-300 hover:border-amber-400 hover:bg-amber-50'
                        }`}>
                        {uploading ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                            <p className="text-sm text-slate-600">Uploading & resizing... {uploadProgress}%</p>
                            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                            </div>
                          </div>
                        ) : formData.image_url ? (
                          <div className="flex flex-col items-center gap-1">
                            <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                            <p className="text-sm text-emerald-600 font-medium">Image uploaded & optimized</p>
                            <p className="text-xs text-slate-400">Click to replace with a different photo</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="w-10 h-10 text-slate-400" />
                            <p className="text-sm font-medium text-slate-600">Click to select a photo</p>
                            <p className="text-xs text-slate-400">JPEG, PNG, GIF, WebP — up to 15MB</p>
                            <p className="text-xs text-amber-600 font-medium">Photos are auto-resized for best display</p>
                          </div>
                        )}
                      </label>
                    </div>
                  )}
                </div>

                {/* Image Preview */}
                {formData.image_url && (
                  <div className="p-3 bg-slate-50 rounded-xl" data-testid="flyer-image-preview">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Preview:</p>
                    <img
                      src={resolveImageUrl(formData.image_url)}
                      alt="Preview"
                      className="w-full h-48 object-contain rounded-lg bg-white"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML += '<div class="flex items-center justify-center h-48 bg-red-50 rounded-lg text-red-500 text-sm gap-2"><span>Could not load image. Check the URL and try again.</span></div>';
                      }}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                  <textarea
                    data-testid="flyer-description-input"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:outline-none transition-colors resize-none"
                    rows={3}
                    placeholder="Brief description of the flyer (optional)"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Event Date</label>
                  <input
                    type="date"
                    data-testid="flyer-event-date-input"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:outline-none transition-colors"
                    value={formData.event_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">Adds a 📅 date pill on the homepage event card. Leave blank to hide.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Link URL</label>
                  <input
                    type="url"
                    data-testid="flyer-link-url-input"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:outline-none transition-colors"
                    placeholder="https://example.com/event-page (optional)"
                    value={formData.link_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">Users will be directed here when they click the flyer</p>
                </div>

                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="is_active"
                    data-testid="flyer-active-checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-5 h-5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-emerald-800">
                    Show this flyer on the homepage
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={resetForm}
                    className="flex-1 py-3 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                  <motion.button type="submit" disabled={uploading || !formData.image_url}
                    data-testid="save-flyer-btn"
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: uploading ? 1 : 1.02 }} whileTap={{ scale: uploading ? 1 : 0.98 }}>
                    <CheckCircle2 className="w-5 h-5" />
                    {editingFlyer ? 'Update Flyer' : 'Add Flyer'}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FlyersSection;
